import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { useState, useCallback, useEffect } from "react";
import { fetchScoutRoutes } from "../../api/smApi";
import { fetchAuthSession } from "../../services/cognitoAuth";
import { useDevSettings } from "./DevSettingsContext";

function downloadGeoJsonFeatureCollection(
  filename: string,
  collection: GeoJSON.FeatureCollection,
): void {
  const blob = new Blob([JSON.stringify(collection, null, 2)], {
    type: "application/geo+json",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

const DEV_PASSWORD = "matewhosails";

interface DevConsoleModalProps {
  open: boolean;
  onClose: () => void;
}

export function DevConsoleModal({ open, onClose }: DevConsoleModalProps) {
  const {
    devModeUnlocked,
    devNetworkTilesEnabled,
    devSpvVotingEnabled,
    setDevModeUnlocked,
    setDevNetworkTilesEnabled,
    setDevSpvVotingEnabled,
  } = useDevSettings();
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [scoutExportLoading, setScoutExportLoading] = useState(false);
  const [scoutExportError, setScoutExportError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setPassword("");
      setError(null);
      setScoutExportLoading(false);
      setScoutExportError(null);
    }
  }, [open]);

  const handleSubmit = useCallback(
    (event?: React.FormEvent) => {
      if (event) {
        event.preventDefault();
      }
      if (devModeUnlocked) return;
      if (password === DEV_PASSWORD) {
        setDevModeUnlocked(true);
        setPassword("");
        setError(null);
      } else {
        setError("Incorrect password");
      }
    },
    [devModeUnlocked, password, setDevModeUnlocked],
  );

  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      onClose();
    }
  };

  const handleSpvVotingToggleChange: React.ChangeEventHandler<
    HTMLInputElement
  > = (event) => {
    setDevSpvVotingEnabled(event.target.checked);
  };

  const handleNetworkTilesToggleChange: React.ChangeEventHandler<
    HTMLInputElement
  > = (event) => {
    setDevNetworkTilesEnabled(event.target.checked);
  };

  const handleExportScoutRoutes = useCallback(async () => {
    setScoutExportError(null);
    setScoutExportLoading(true);
    try {
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken?.toString();
      if (!accessToken) {
        setScoutExportError("Sign in required to export scout routes.");
        return;
      }
      const collection = await fetchScoutRoutes(accessToken);
      const exportCollection: GeoJSON.FeatureCollection = {
        ...collection,
        features: collection.features.map((feature) => ({
          ...feature,
          properties: {
            ...feature.properties,
            scout_route: true,
          },
        })),
      };
      const date = new Date().toISOString().slice(0, 10);
      downloadGeoJsonFeatureCollection(
        `scout-routes-${date}.geojson`,
        exportCollection,
      );
    } catch (exportError) {
      const message =
        exportError instanceof Error
          ? exportError.message
          : "Failed to export scout routes.";
      setScoutExportError(message);
    } finally {
      setScoutExportLoading(false);
    }
  }, []);

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { timeout: 200 } }}
      keepMounted={false}
    >
      <Box
        onKeyDown={handleKeyDown}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          boxShadow: 24,
          borderRadius: 2,
          p: 3,
          minWidth: 320,
          maxWidth: "90vw",
          outline: "none",
        }}
      >
        {!devModeUnlocked ? (
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            <Typography variant="h6" component="h2">
              Developer Console
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Enter developer password to unlock advanced settings.
            </Typography>
            <TextField
              autoFocus
              type="password"
              label="Password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                if (error) setError(null);
              }}
              error={Boolean(error)}
              helperText={error ?? " "}
              size="small"
            />
            <Box
              sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 1 }}
            >
              <Button onClick={onClose} color="inherit">
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={!password}
              >
                Unlock
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h6" component="h2">
              Developer Settings
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Typography variant="subtitle2">Experimental layers</Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography variant="body2">
                    Enable network quality tiles (preview)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Shows network tiles controls in map layer menu.
                  </Typography>
                </Box>
                <Box component="label" sx={{ display: "flex", alignItems: "center" }}>
                  <input
                    type="checkbox"
                    checked={devNetworkTilesEnabled}
                    onChange={handleNetworkTilesToggleChange}
                  />
                </Box>
              </Box>
              <Typography variant="subtitle2" sx={{ pt: 1 }}>
                Harbor voting
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography variant="body2">
                    Enable SPV harbor voting (preview)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Shows guest harbor rating and voting UI on the map.
                  </Typography>
                </Box>
                <Box component="label" sx={{ display: "flex", alignItems: "center" }}>
                  <input
                    type="checkbox"
                    checked={devSpvVotingEnabled}
                    onChange={handleSpvVotingToggleChange}
                  />
                </Box>
              </Box>
              <Typography variant="subtitle2" sx={{ pt: 1 }}>
                Data export
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography variant="body2">
                  Export scout routes as GeoJSON FeatureCollection
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Downloads all scout routes from the API as line features
                  (requires sign-in).
                </Typography>
                {scoutExportError ? (
                  <Typography variant="caption" color="error">
                    {scoutExportError}
                  </Typography>
                ) : null}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => void handleExportScoutRoutes()}
                  disabled={scoutExportLoading}
                  sx={{ alignSelf: "flex-start" }}
                  startIcon={
                    scoutExportLoading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  {scoutExportLoading ? "Exporting…" : "Export scout routes"}
                </Button>
              </Box>
            </Box>
            <Box
              sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 1 }}
            >
              <Button onClick={onClose} color="inherit">
                Close
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Modal>
  );
}

